import {
  Box,
  Chip,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useMemo } from 'react';
import { CHAINS, CHAIN_LABELS } from '../../../wallets/addressValidation';
import AssetIcon from '../../../shared/AssetIcon';

const SPACING = {
  XS: 1,
  SM: 2,
  MD: 3,
  LG: 4,
};

const DESIGN_COLORS = {
  BACKGROUND: '#f8f9fa',
  SURFACE: '#ffffff',
  BORDER: '#e8eaed',
  TEXT_SECONDARY: '#5f6368',
  TEXT_PRIMARY: '#202124',
  ACCENT_PRIMARY: '#1f2937',
  ACCENT_SECONDARY: '#6366f1',
};

const CARD_SX = {
  borderRadius: '12px',
  border: `1px solid ${DESIGN_COLORS.BORDER}`,
  backgroundColor: DESIGN_COLORS.SURFACE,
  transition: 'all 0.3s ease',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
  '&:hover': {
    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)',
  },
};

const formatFiat = (value) => {
  if (value == null || value === '') return '-';
  return Number(value).toLocaleString('de-DE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 2,
  });
};

const formatBalance = (value) => {
  if (value == null || value === '') return '-';
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return num.toLocaleString('de-DE', { maximumSignificantDigits: 6 });
};

const truncateMiddle = (value, head = 6, tail = 4) => {
  if (!value) return '';
  if (value.length <= head + tail + 1) return value;
  return `${value.slice(0, head)}…${value.slice(-tail)}`;
};

const tokenInitials = (token) => {
  const base = token?.symbol || token?.name || token?.address || '?';
  return base.slice(0, 3).toUpperCase();
};

const tokenLabel = (token) =>
  token?.symbol || token?.name || token?.address || 'Unknown';

const summarizeChains = (addresses) => {
  const counts = addresses.reduce((acc, address) => {
    const chain = address?.chain;
    if (!chain) return acc;
    acc[chain] = (acc[chain] || 0) + 1;
    return acc;
  }, {});
  return Object.entries(counts).map(([chain, count]) => ({
    chain,
    count,
    label: CHAIN_LABELS[chain] || chain,
  }));
};

const HEAD_CELL_SX = {
  backgroundColor: DESIGN_COLORS.BACKGROUND,
  color: DESIGN_COLORS.TEXT_SECONDARY,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  fontSize: '0.7rem',
  fontWeight: 700,
};

const TABLE_SX = {
  '& tr:hover': {
    backgroundColor: DESIGN_COLORS.BACKGROUND,
  },
  '& td, & th': {
    borderColor: DESIGN_COLORS.BORDER,
  },
};

/**
 * Holdings aggregated across every wallet in the portfolio, one row per token
 * (answers "how much USDC do I hold in total"). Rows arrive already sorted by
 * fiatValue descending; the total isn't in the payload, so it's summed here.
 */
function TokenBalancesTable({ tokenBalances, isMobile }) {
  const rows = useMemo(
    () => (Array.isArray(tokenBalances) ? tokenBalances : []),
    [tokenBalances],
  );
  const total = useMemo(
    () =>
      rows.reduce((sum, row) => {
        const value = Number(row?.fiatValue || 0);
        return sum + (Number.isFinite(value) ? value : 0);
      }, 0),
    [rows],
  );

  return (
    <Paper
      elevation={0}
      sx={{ ...CARD_SX, mb: SPACING.MD, overflow: 'hidden' }}
    >
      <Box
        sx={{
          p: SPACING.MD,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: SPACING.SM,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: SPACING.SM,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              width: '4px',
              height: '32px',
              borderRadius: '2px',
              flexShrink: 0,
              background: `linear-gradient(135deg, ${DESIGN_COLORS.ACCENT_SECONDARY} 0%, ${DESIGN_COLORS.ACCENT_PRIMARY} 100%)`,
            }}
          />
          <Typography
            variant="h6"
            noWrap
            sx={{ color: DESIGN_COLORS.TEXT_PRIMARY, fontWeight: 700 }}
          >
            All wallets by token
          </Typography>
        </Box>
        <Typography
          variant="subtitle1"
          sx={{
            color: DESIGN_COLORS.TEXT_PRIMARY,
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {formatFiat(total)}
        </Typography>
      </Box>

      <Divider sx={{ borderColor: DESIGN_COLORS.BORDER }} />

      {rows.length === 0 ? (
        <Box sx={{ p: SPACING.LG, textAlign: 'center' }}>
          <Typography
            variant="body2"
            sx={{ color: DESIGN_COLORS.TEXT_SECONDARY }}
          >
            No balances yet
          </Typography>
        </Box>
      ) : isMobile ? (
        <Stack divider={<Divider sx={{ borderColor: DESIGN_COLORS.BORDER }} />}>
          {rows.map(({ token, balance, fiatPrice, fiatValue }) => (
            <Box
              key={token?.id ?? token?.address}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: SPACING.SM,
                px: SPACING.MD,
                py: 1.25,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: SPACING.XS,
                  minWidth: 0,
                }}
              >
                <AssetIcon
                  iconUrl={token?.iconUrl}
                  label={tokenLabel(token)}
                  initials={tokenInitials(token)}
                  size={32}
                  fontSize="0.65rem"
                />
                <Stack spacing={0} sx={{ minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{
                      color: DESIGN_COLORS.TEXT_PRIMARY,
                      fontWeight: 600,
                      lineHeight: 1.2,
                    }}
                  >
                    {token?.name || tokenLabel(token)}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: DESIGN_COLORS.TEXT_SECONDARY }}
                  >
                    {token?.symbol || ''}
                  </Typography>
                </Stack>
              </Box>
              <Stack spacing={0} sx={{ alignItems: 'flex-end', flexShrink: 0 }}>
                <Typography
                  variant="body2"
                  sx={{ color: DESIGN_COLORS.TEXT_PRIMARY, fontWeight: 700 }}
                >
                  {fiatValue ? formatFiat(fiatValue) : '-'}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: DESIGN_COLORS.TEXT_SECONDARY }}
                >
                  {`${formatBalance(balance)} · ${
                    fiatPrice ? formatFiat(fiatPrice) : '-'
                  }`}
                </Typography>
              </Stack>
            </Box>
          ))}
        </Stack>
      ) : (
        <Table size="small" sx={TABLE_SX}>
          <TableHead>
            <TableRow>
              {['Token', 'Balance', 'Price', 'Value'].map((head, idx) => (
                <TableCell
                  key={head}
                  align={idx === 0 ? 'left' : 'right'}
                  sx={HEAD_CELL_SX}
                >
                  {head}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(({ token, balance, fiatPrice, fiatValue }) => (
              <TableRow key={token?.id ?? token?.address}>
                <TableCell sx={{ py: 1 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: SPACING.XS,
                    }}
                  >
                    <AssetIcon
                      iconUrl={token?.iconUrl}
                      label={tokenLabel(token)}
                      initials={tokenInitials(token)}
                      size={28}
                      fontSize="0.65rem"
                    />
                    <Stack spacing={0}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: DESIGN_COLORS.TEXT_PRIMARY,
                          fontWeight: 600,
                          lineHeight: 1.2,
                        }}
                      >
                        {token?.name || tokenLabel(token)}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: DESIGN_COLORS.TEXT_SECONDARY }}
                      >
                        {token?.symbol || ''}
                      </Typography>
                    </Stack>
                  </Box>
                </TableCell>
                <TableCell align="right" sx={{ py: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: DESIGN_COLORS.TEXT_PRIMARY,
                      fontWeight: 600,
                    }}
                  >
                    {formatBalance(balance)}
                  </Typography>
                </TableCell>
                <TableCell align="right" sx={{ py: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{ color: DESIGN_COLORS.TEXT_SECONDARY }}
                  >
                    {fiatPrice ? formatFiat(fiatPrice) : '-'}
                  </Typography>
                </TableCell>
                <TableCell align="right" sx={{ py: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: DESIGN_COLORS.TEXT_PRIMARY,
                      fontWeight: 600,
                    }}
                  >
                    {fiatValue ? formatFiat(fiatValue) : '-'}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell sx={{ ...HEAD_CELL_SX, py: 1 }}>Total</TableCell>
              <TableCell sx={HEAD_CELL_SX} />
              <TableCell sx={HEAD_CELL_SX} />
              <TableCell
                align="right"
                sx={{
                  ...HEAD_CELL_SX,
                  color: DESIGN_COLORS.TEXT_PRIMARY,
                  fontSize: '0.8rem',
                }}
              >
                {formatFiat(total)}
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      )}
    </Paper>
  );
}

export default function WalletsComponent({ wallets, tokenBalances }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const hasWallets = Array.isArray(wallets) && wallets.length > 0;

  return (
    <Box
      sx={{
        flexGrow: 1,
        p: SPACING.MD,
        px: isMobile ? 0 : SPACING.MD,
        backgroundColor: DESIGN_COLORS.BACKGROUND,
        borderRadius: isMobile ? 0 : '8px',
      }}
    >
      {!hasWallets ? (
        <Paper
          elevation={0}
          sx={{
            ...CARD_SX,
            p: SPACING.LG,
            textAlign: 'center',
          }}
        >
          <Typography
            variant="body1"
            sx={{ color: DESIGN_COLORS.TEXT_SECONDARY }}
          >
            No wallets to display
          </Typography>
        </Paper>
      ) : (
        <>
          <TokenBalancesTable
            tokenBalances={tokenBalances}
            isMobile={isMobile}
          />
          {wallets.map((entry) => {
            const wallet = entry?.wallet || {};
            const balances = entry?.balances || [];
            const addresses = wallet.addresses || [];
            const chainSummary = summarizeChains(addresses);

            return (
              <Paper
                key={wallet.id}
                elevation={0}
                sx={{ ...CARD_SX, mb: SPACING.MD, overflow: 'hidden' }}
              >
                <Box sx={{ p: SPACING.MD }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: SPACING.SM,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: SPACING.SM,
                        minWidth: 0,
                      }}
                    >
                      <Box
                        sx={{
                          width: '4px',
                          height: '32px',
                          borderRadius: '2px',
                          flexShrink: 0,
                          background: `linear-gradient(135deg, ${DESIGN_COLORS.ACCENT_SECONDARY} 0%, ${DESIGN_COLORS.ACCENT_PRIMARY} 100%)`,
                        }}
                      />
                      <Typography
                        variant="h6"
                        noWrap
                        sx={{
                          color: DESIGN_COLORS.TEXT_PRIMARY,
                          fontWeight: 700,
                        }}
                      >
                        {wallet.name}
                      </Typography>
                    </Box>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        color: DESIGN_COLORS.TEXT_PRIMARY,
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      {formatFiat(entry.fiatValue)}
                    </Typography>
                  </Box>

                  {chainSummary.length > 0 && (
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ mt: SPACING.SM, flexWrap: 'wrap', gap: 1 }}
                    >
                      {chainSummary.map(({ chain, count, label }) => (
                        <Chip
                          key={chain}
                          size="small"
                          label={`${label} · ${count}`}
                          sx={{
                            backgroundColor: DESIGN_COLORS.BACKGROUND,
                            color: DESIGN_COLORS.TEXT_SECONDARY,
                            fontWeight: 600,
                          }}
                        />
                      ))}
                    </Stack>
                  )}

                  {addresses.length > 0 ? (
                    <Stack spacing={0.75} sx={{ mt: SPACING.SM }}>
                      {addresses.map((address) => (
                        <Box
                          key={address.id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            flexWrap: 'wrap',
                          }}
                        >
                          <Tooltip title={address.value} placement="top-start">
                            <Typography
                              variant="body2"
                              sx={{
                                color: DESIGN_COLORS.TEXT_SECONDARY,
                                fontFamily: 'monospace',
                              }}
                            >
                              {`${CHAIN_LABELS[address.chain] || address.chain}: ${truncateMiddle(
                                address.value,
                              )}`}
                            </Typography>
                          </Tooltip>
                          {address.chain === CHAINS.BITCOIN && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: DESIGN_COLORS.TEXT_SECONDARY,
                                fontStyle: 'italic',
                              }}
                            >
                              balance not computed yet
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        mt: SPACING.SM,
                        color: DESIGN_COLORS.TEXT_SECONDARY,
                      }}
                    >
                      No addresses
                    </Typography>
                  )}
                </Box>

                <Divider sx={{ borderColor: DESIGN_COLORS.BORDER }} />

                {isMobile ? (
                  <Stack
                    divider={
                      <Divider sx={{ borderColor: DESIGN_COLORS.BORDER }} />
                    }
                  >
                    {balances.map(
                      ({ token, balance, fiatPrice, fiatValue }) => (
                        <Box
                          key={token?.id || token?.address}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: SPACING.SM,
                            px: SPACING.MD,
                            py: 1.25,
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: SPACING.XS,
                              minWidth: 0,
                            }}
                          >
                            <AssetIcon
                              iconUrl={token?.iconUrl}
                              label={tokenLabel(token)}
                              initials={tokenInitials(token)}
                              size={32}
                              fontSize="0.65rem"
                            />
                            <Stack spacing={0} sx={{ minWidth: 0 }}>
                              <Typography
                                variant="body2"
                                noWrap
                                sx={{
                                  color: DESIGN_COLORS.TEXT_PRIMARY,
                                  fontWeight: 600,
                                  lineHeight: 1.2,
                                }}
                              >
                                {token?.name || tokenLabel(token)}
                              </Typography>
                            </Stack>
                          </Box>
                          <Stack
                            spacing={0}
                            sx={{ alignItems: 'flex-end', flexShrink: 0 }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color: DESIGN_COLORS.TEXT_PRIMARY,
                                fontWeight: 700,
                              }}
                            >
                              {fiatValue ? formatFiat(fiatValue) : '-'}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: DESIGN_COLORS.TEXT_SECONDARY }}
                            >
                              {`${formatBalance(balance)} · ${
                                fiatPrice ? formatFiat(fiatPrice) : '-'
                              }`}
                            </Typography>
                          </Stack>
                        </Box>
                      ),
                    )}
                  </Stack>
                ) : (
                  <Table
                    size="small"
                    sx={{
                      '& tr:hover': {
                        backgroundColor: DESIGN_COLORS.BACKGROUND,
                      },
                      '& td, & th': {
                        borderColor: DESIGN_COLORS.BORDER,
                      },
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        {['Token', 'Balance', 'Price', 'Value'].map(
                          (head, idx) => (
                            <TableCell
                              key={head}
                              align={idx === 0 ? 'left' : 'right'}
                              sx={{
                                backgroundColor: DESIGN_COLORS.BACKGROUND,
                                color: DESIGN_COLORS.TEXT_SECONDARY,
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                fontSize: '0.7rem',
                                fontWeight: 700,
                              }}
                            >
                              {head}
                            </TableCell>
                          ),
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {balances.map(
                        ({ token, balance, fiatPrice, fiatValue }) => (
                          <TableRow key={token?.id || token?.address}>
                            <TableCell sx={{ py: 1 }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: SPACING.XS,
                                }}
                              >
                                <AssetIcon
                                  iconUrl={token?.iconUrl}
                                  label={tokenLabel(token)}
                                  initials={tokenInitials(token)}
                                  size={28}
                                  fontSize="0.65rem"
                                />
                                <Stack spacing={0}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: DESIGN_COLORS.TEXT_PRIMARY,
                                      fontWeight: 600,
                                      lineHeight: 1.2,
                                    }}
                                  >
                                    {token?.name || tokenLabel(token)}
                                  </Typography>
                                </Stack>
                              </Box>
                            </TableCell>
                            <TableCell align="right" sx={{ py: 1 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: DESIGN_COLORS.TEXT_PRIMARY,
                                  fontWeight: 600,
                                }}
                              >
                                {formatBalance(balance)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ py: 1 }}>
                              <Typography
                                variant="body2"
                                sx={{ color: DESIGN_COLORS.TEXT_SECONDARY }}
                              >
                                {fiatPrice ? formatFiat(fiatPrice) : '-'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ py: 1 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: DESIGN_COLORS.TEXT_PRIMARY,
                                  fontWeight: 600,
                                }}
                              >
                                {fiatValue ? formatFiat(fiatValue) : '-'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ),
                      )}
                    </TableBody>
                  </Table>
                )}
              </Paper>
            );
          })}
        </>
      )}
    </Box>
  );
}
